/**
 * Mermaid Templates
 *
 * The starting Mermaid_Sources offered in the new-diagram dialog when the
 * Mermaid kind is chosen (Requirement 4.2). Unlike the Excalidraw templates
 * (which build a full scene, see `templates/index.ts`), a Mermaid template is
 * just a name and a starting source string, so this is a plain registry with
 * no scene-building or cloning.
 *
 * v1 ships "Blank" (a one-node flowchart), a flowchart starter, and a
 * sequence-diagram starter — the two most common diagram types. Every starter
 * must be a valid, non-empty Mermaid_Source: creating a plot renders it with
 * `mermaid.render`, which throws on text with no diagram type, and the backend
 * rejects an empty source.
 */

/**
 * A Mermaid starter offered in the new-diagram dialog.
 */
export interface MermaidTemplate {
  /** A stable key for the template (used in UI state and tests). */
  id: string
  /** The human-readable name shown in the dialog. */
  name: string
  /**
   * The starting Mermaid_Source. Never empty: it is rendered when the plot is
   * created, and Mermaid cannot render text with no diagram type.
   */
  source: string
}

/**
 * The Mermaid templates offered in the new-diagram dialog, in display order:
 * Blank first, then a flowchart and a sequence-diagram starter.
 */
export const MERMAID_TEMPLATES: readonly MermaidTemplate[] = [
  {
    id: "blank",
    name: "Blank",
    // The smallest source that renders: Mermaid needs a diagram type, and a
    // node keeps the first render from being an empty, zero-size SVG.
    source: ["flowchart TD", "    A[Start]"].join("\n"),
  },
  {
    id: "flowchart",
    name: "Flowchart",
    source: ["flowchart TD", "    A[Start] --> B{Decision}", "    B -->|Yes| C[Do this]", "    B -->|No| D[Do that]"].join(
      "\n",
    ),
  },
  {
    id: "sequence",
    name: "Sequence diagram",
    source: [
      "sequenceDiagram",
      "    participant A as Service A",
      "    participant B as Service B",
      "    A->>B: Request",
      "    B-->>A: Response",
    ].join("\n"),
  },
]
